import { FormattedMessage, FormattedDate } from 'react-intl';

import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals/MoneyNoDecimals';
import { Tooltip } from '../../../ui/tooltip/Tooltip';
import { ChooseLoanAmount } from '../../../sharedDashboardComponents/slider/chooseLoanAmount/ChooseLoanAmount';
import messages from './CustomiseOffer.messages';
import { FormalOffer } from '../../MyOffers.types';
import { GetFormalOffersResponse } from '@iwoca/lapi-client/edge';
import { MIN_DRAWDOWN_AMOUNT } from '../../../minDrawdownAmount';
import { OfferChoiceButtons } from './offerChoiceButtons/OfferChoiceButtons';
import { getMaxAvailableCash, isInterestOnly } from '../../MyOffers.helpers';
import { SelectOfferType } from './selectOfferType/selectOfferType';
import { useState } from 'react';
import { getCountry } from '@iwIntlProvider/CountryProvider';

type CustomiseOfferProps = {
    drawdownAmount: number;
    setDrawdownAmount: (drawdownAmount: number) => void;
    selectedOffer: FormalOffer | null;
    updateSelectedOffer: (offer: FormalOffer | null) => void;
    formalOffers: NonNullable<GetFormalOffersResponse['data']>;
};

type FormalOffers = NonNullable<
    GetFormalOffersResponse['data']
>['formal_offers'];

export const CustomiseOffer = ({
    drawdownAmount,
    setDrawdownAmount,
    selectedOffer,
    updateSelectedOffer,
    formalOffers
}: CustomiseOfferProps) => {
    const maxAvailableCash = getMaxAvailableCash(formalOffers!.formal_offers);

    //set default initial Offers when there are only 2 formal offers
    const initalOfferChoices =
        formalOffers.formal_offers.length === 2 ||
        (getCountry() === 'UK' && formalOffers.formal_offers.length > 1)
            ? formalOffers.formal_offers
            : [];

    const [offerChoices, setOfferChoices] = useState<FormalOffers | []>(
        initalOfferChoices
    );

    if (formalOffers.formal_offers.length === 1) {
        //default the selected offer to the first offer since user has no choice
        updateSelectedOffer(formalOffers!.formal_offers[0]);
    }

    //needed to indicate the copy that is used in section headings
    const hasInterestOnlyChoice = Boolean(
        formalOffers!.formal_offers.find((formal_offer) =>
            isInterestOnly(formal_offer)
        )
    );

    return (
        <>
            <ChooseLoanAmount
                setDrawdownAmount={setDrawdownAmount}
                drawdownAmount={drawdownAmount}
                minimumDrawdownAmount={MIN_DRAWDOWN_AMOUNT}
                maximumDrawdownAmount={maxAvailableCash}
                inputTooltip={
                    <Tooltip
                        data-action-name="Input tooltip"
                        message={
                            <FormattedMessage
                                {...messages.inputTooltip}
                                values={{
                                    amount: (
                                        <MoneyNoDecimals
                                            value={maxAvailableCash}
                                        />
                                    ),
                                    date: (
                                        <FormattedDate
                                            value={
                                                selectedOffer
                                                    ? new Date(
                                                          selectedOffer.timeout!
                                                      ).toISOString()
                                                    : new Date()
                                            }
                                            day="2-digit"
                                            month="long"
                                            year="numeric"
                                        />
                                    )
                                }}
                            />
                        }
                    />
                }
            />

            {getCountry() === 'DE' &&
                formalOffers!.formal_offers.length > 2 && (
                    <SelectOfferType
                        formalOffers={formalOffers!.formal_offers}
                        setOfferChoices={setOfferChoices}
                        drawdownAmount={drawdownAmount}
                        updateSelectedOffer={updateSelectedOffer}
                    />
                )}

            {offerChoices.length > 0 && (
                <OfferChoiceButtons
                    updateSelectedOffer={updateSelectedOffer}
                    formalOffers={offerChoices}
                    drawdownAmount={drawdownAmount}
                    hasInterestOnlyChoice={hasInterestOnlyChoice}
                />
            )}
        </>
    );
};
